import React, { useState, useRef } from "react";
import { importVideos, exportVideos, downloadTemplate } from "../lib/api";
import { toast } from "sonner";
import { Upload, Download, FileXls, CheckCircle, XCircle, Warning, File } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

const TEMPLATES = [
  { type: "videos", name: "Video Import Template", description: "Import videos with title, channel, stage, editor, etc." },
  { type: "channels", name: "Channel Import Template", description: "Import multiple channels at once" },
  { type: "editors", name: "Editor Import Template", description: "Import editor profiles" },
  { type: "ideas", name: "Ideas Import Template", description: "Bulk import video ideas" },
];

const EXPORTS = [
  { type: "videos", name: "Export All Videos", description: "Download all videos as Excel" },
  { type: "shorts", name: "Export Shorts", description: "Download all shorts as Excel" },
];

export default function ImportExport() {
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [exporting, setExporting] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImporting(true);
    setImportResult(null);

    try {
      const response = await importVideos(file);
      setImportResult(response.data);
      if (response.data.imported > 0) {
        toast.success(`Imported ${response.data.imported} videos`);
      }
      if (response.data.skipped > 0 || response.data.errors?.length > 0) {
        toast.warning(`${response.data.skipped} rows skipped`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
      setImportResult({ imported: 0, skipped: 0, errors: ["Import failed. Please check your file format."] });
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDownloadTemplate = async (type) => {
    try {
      const response = await downloadTemplate(type);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${type}_import_template.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Template downloaded");
    } catch (err) {
      toast.error("Download failed");
    }
  };

  const handleExport = async (type) => {
    setExporting(true);
    try {
      const params = type === "shorts" ? { type: "short" } : {};
      const response = await exportVideos(params);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${type}_export_${new Date().toISOString().split("T")[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Export downloaded");
    } catch (err) {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      <h1 className="font-heading text-2xl font-semibold text-white">Import / Export</h1>

      <Tabs defaultValue="import" className="space-y-6">
        <TabsList className="bg-white/5 border border-white/10">
          <TabsTrigger value="import" className="data-[state=active]:bg-blue-600">Import</TabsTrigger>
          <TabsTrigger value="export" className="data-[state=active]:bg-blue-600">Export</TabsTrigger>
          <TabsTrigger value="templates" className="data-[state=active]:bg-blue-600">Templates</TabsTrigger>
        </TabsList>

        <TabsContent value="import" className="space-y-6">
          {/* Upload Area */}
          <div className="surface-card p-8">
            <div
              className="border-2 border-dashed border-white/10 rounded-xl p-8 text-center hover:border-blue-500/50 transition-colors cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.csv"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="import-file-input"
              />
              <Upload size={48} className="text-gray-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-white mb-2">
                {importing ? "Importing..." : "Drop your Excel file here"}
              </h3>
              <p className="text-gray-400 text-sm mb-4">
                Supports .xlsx and .csv files
              </p>
              <Button className="btn-primary" disabled={importing}>
                {importing ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    <Upload size={18} className="mr-2" />
                    Select File
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Import Result */}
          {importResult && (
            <div className="surface-card p-6 space-y-4">
              <h3 className="font-heading text-lg font-medium text-white">Import Result</h3>
              
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/20">
                  <div className="flex items-center gap-2 mb-1">
                    <CheckCircle size={18} className="text-green-400" />
                    <span className="text-green-400 font-medium">Imported</span>
                  </div>
                  <p className="text-2xl font-heading font-semibold text-white">{importResult.imported}</p>
                </div>
                <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/20">
                  <div className="flex items-center gap-2 mb-1">
                    <Warning size={18} className="text-amber-400" />
                    <span className="text-amber-400 font-medium">Skipped</span>
                  </div>
                  <p className="text-2xl font-heading font-semibold text-white">{importResult.skipped}</p>
                </div>
                <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/20">
                  <div className="flex items-center gap-2 mb-1">
                    <XCircle size={18} className="text-red-400" />
                    <span className="text-red-400 font-medium">Errors</span>
                  </div>
                  <p className="text-2xl font-heading font-semibold text-white">{importResult.errors?.length || 0}</p>
                </div>
              </div>

              {importResult.errors?.length > 0 && (
                <div className="mt-4 p-4 rounded-lg bg-red-500/5 border border-red-500/10 max-h-48 overflow-y-auto">
                  <h4 className="text-sm font-medium text-red-400 mb-2">Error Details:</h4>
                  <ul className="space-y-1 text-sm text-gray-400">
                    {importResult.errors.map((err, idx) => (
                      <li key={`err-${idx}`}>• {err}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Instructions */}
          <div className="surface-card p-6">
            <h3 className="font-heading text-lg font-medium text-white mb-4">Import Instructions</h3>
            <ol className="list-decimal list-inside space-y-2 text-gray-400 text-sm">
              <li>Download the appropriate template from the Templates tab</li>
              <li>Fill in your data following the template format</li>
              <li>Save the file as .xlsx or .csv</li>
              <li>Upload the file using the drop zone above</li>
              <li>Review the import results and fix any errors</li>
            </ol>
          </div>
        </TabsContent>

        <TabsContent value="export" className="space-y-4">
          {EXPORTS.map((exp) => (
            <div key={exp.type} className="surface-card p-6 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center">
                  <FileXls size={24} className="text-green-400" />
                </div>
                <div>
                  <h3 className="text-white font-medium">{exp.name}</h3>
                  <p className="text-sm text-gray-400">{exp.description}</p>
                </div>
              </div>
              <Button
                onClick={() => handleExport(exp.type)}
                className="btn-primary"
                disabled={exporting}
                data-testid={`export-${exp.type}-btn`}
              >
                <Download size={18} className="mr-2" />
                Export
              </Button>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="templates" className="space-y-4">
          {TEMPLATES.map((template) => (
            <div key={template.type} className="surface-card p-6 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
                  <File size={24} className="text-blue-400" />
                </div>
                <div>
                  <h3 className="text-white font-medium">{template.name}</h3>
                  <p className="text-sm text-gray-400">{template.description}</p>
                </div>
              </div>
              <Button
                onClick={() => handleDownloadTemplate(template.type)}
                variant="outline"
                className="btn-secondary"
                data-testid={`template-${template.type}-btn`}
              >
                <Download size={18} className="mr-2" />
                Download
              </Button>
            </div>
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
}
